import React, { useRef, useState } from "react";
import { useNavigate } from 'react-router-dom';
import { Container, Row, Col, Form, Button, Card } from 'react-bootstrap';
import './FirstProfile.css';
import { useAuth } from '../../contexts/AuthContext';
import { updateUserProfile } from '../../utils/firestoreUser';
import { uploadToCloudinary } from '../../utils/cloudinary';
import CreatableSelect from 'react-select/creatable';
import ISO6391 from 'iso-639-1';
import { getNames as getCountryNames, getCode as getCountryCode } from 'country-list';
import countryData from 'country-telephone-data';

export default function CreateProfile() {
    const [image, setImage] = useState(null);
    const [isUploading, setIsUploading] = useState(false);
    const [about, setAbout] = useState('');
    const [phoneNumber, setPhoneNumber] = useState('');
    const [countryCode, setCountryCode] = useState('+972');
    const [profileName, setProfileName] = useState('');
    const [languages, setLanguages] = useState([]);
    const [languagesTouched, setLanguagesTouched] = useState(false);
    const [profileNameTouched, setProfileNameTouched] = useState(false);
    const [phoneTouched, setPhoneTouched] = useState(false);
    const fileInput = useRef();
    const textareaRef = useRef();
    const navigate = useNavigate();
    const { currentUser } = useAuth();
    const BLANK_PROFILE_IMG = '/blankprofile.png';

    const LANGUAGE_OPTIONS = ISO6391.getAllCodes().map(code => ({
        value: ISO6391.getName(code),
        label: ISO6391.getName(code)
    }));

    // רשימת קידומות גלובלית מלאה
    const COUNTRY_DIAL_CODES = countryData.allCountries.map(c => ({
        name: c.name,
        dialCode: '+' + c.dialCode,
        code: c.iso2 ? c.iso2.toUpperCase() : '',
    }));

    const handleImageChange = async (e) => {
        if (e.target.files && e.target.files[0]) {
            try {
                const file = e.target.files[0];
                setIsUploading(true);
                const url = await uploadToCloudinary(file);
                setImage(url);
            } catch (error) {
                console.error('שגיאה בהעלאת תמונה:', error);
                alert(error.message || 'העלאת התמונה נכשלה. אנא נסה שוב.');
                setImage('');
            } finally {
                setIsUploading(false);
            }
        }
    };

    // פונקציה שמגדילה אוטומטית את גובה הטקסטאראה
    const handleTextareaChange = (e) => {
        setAbout(e.target.value);
        textareaRef.current.style.height = 'auto';
        textareaRef.current.style.height = textareaRef.current.scrollHeight + 'px';
    };

    const handleContinue = async () => {
        setLanguagesTouched(true);
        setProfileNameTouched(true);
        setPhoneTouched(true);
        if (!currentUser) return;
        if (!languages.length || !profileName.trim() || !phoneNumber.trim()) return;
        await updateUserProfile(currentUser.uid, {
            profileImg: image || BLANK_PROFILE_IMG,
            name: profileName,
            phone: countryCode + phoneNumber,
            about: about,
            languages: languages.map(l => l.value).join(', ')
        });
        window.scrollTo(0, 0);
        navigate('/signup-success');
    };

    // פונקציה להמרת קוד מדינה לדגל יוניקוד
    function getFlagEmoji(countryCode) {
        if (!countryCode || countryCode.length !== 2) return '';
        return String.fromCodePoint(
            ...countryCode
                .toUpperCase()
                .split('')
                .map(char => 127397 + char.charCodeAt())
        );
    }

    return (
        <div
            className="profile-bg min-vh-100 d-flex align-items-center"
            style={{ background: "url('/background.png') center center/cover no-repeat, #eaf3fb" }}
        >
            <Container>
                <Row className="justify-content-center">
                    <Col md={8} lg={6}>
                        <Card className="profile-card p-4">
                            <Card.Body>
                                <h1 className="profile-title text-center mb-4">Create your profile</h1>
                                <p className="profile-desc text-center mb-4">
                                    Create a personal or shared profile – as an individual, couple, family, or group – to host or join Shabbat dinners.
                                </p>

                                <Form>
                                    <Form.Group className="mb-4">
                                        <Form.Label className="profile-label">
                                            Upload your profile picture
                                        </Form.Label>
                                        <div 
                                            className="profile-picture-wrapper mx-auto" 
                                            onClick={() => fileInput.current.click()}
                                            style={{ cursor: 'pointer' }}
                                        >
                                            {isUploading ? (
                                                <div className="profile-picture profile-picture-loading" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f0f0f0', width: 150, height: 150, borderRadius: '50%' }}>
                                                    <div className="spinner-border text-primary" role="status">
                                                        <span className="visually-hidden">Uploading...</span>
                                                    </div>
                                                </div>
                                            ) : image ? (
                                                <img src={image} alt="Profile" className="profile-picture rounded-circle" />
                                            ) : (
                                                <div className="profile-picture-placeholder">
                                                    <svg width="80" height="80" viewBox="0 0 90 90" fill="none">
                                                        <circle cx="45" cy="45" r="42" stroke="#bdbdbd" strokeWidth="2"/>
                                                        <circle cx="45" cy="38" r="15" stroke="#bdbdbd" strokeWidth="2"/>
                                                        <path d="M20 70c0-12 13-22 25-22s25 10 25 22" stroke="#bdbdbd" strokeWidth="2"/>
                                                    </svg>
                                                </div>
                                            )}
                                            <input
                                                type="file"
                                                accept="image/*"
                                                ref={fileInput}
                                                style={{ display: "none" }}
                                                onChange={handleImageChange}
                                            />
                                        </div>
                                    </Form.Group>

                                    <Form.Group className="mb-4">
                                        <Form.Label className="profile-label">
                                            Profile name <span style={{color:'red'}}>*</span>
                                        </Form.Label>
                                        <Form.Control 
                                            type="text" 
                                            className="profile-input"
                                            value={profileName}
                                            onChange={e => setProfileName(e.target.value)}
                                            onBlur={() => setProfileNameTouched(true)}
                                        />
                                        {profileNameTouched && !profileName.trim() && (
                                            <div style={{color:'red', fontSize:13, marginTop:4}}>Please enter your profile name</div>
                                        )}
                                    </Form.Group>

                                    <Form.Group className="mb-4">
                                        <Form.Label className="profile-label">
                                            Phone Number <span style={{color:'red'}}>*</span>
                                        </Form.Label>
                                        <div style={{ display: 'flex', gap: '8px' }}>
                                            <Form.Select
                                                value={countryCode}
                                                onChange={e => setCountryCode(e.target.value)}
                                                style={{ maxWidth: 250 }}
                                            >
                                                {COUNTRY_DIAL_CODES.map(opt => (
                                                    <option key={opt.code + opt.dialCode} value={opt.dialCode}>
                                                        {opt.code && opt.code.length === 2 ? getFlagEmoji(opt.code) + ' ' : ''}{opt.name} {opt.dialCode}
                                                    </option>
                                                ))}
                                            </Form.Select>
                                            <Form.Control 
                                                type="tel" 
                                                className="profile-input"
                                                placeholder="Enter your phone number"
                                                value={phoneNumber}
                                                onChange={(e) => setPhoneNumber(e.target.value)}
                                                style={{ flex: 1 }}
                                                onBlur={() => setPhoneTouched(true)}
                                            />
                                        </div>
                                        {phoneTouched && !phoneNumber.trim() && (
                                            <div style={{color:'red', fontSize:13, marginTop:4}}>Please enter your phone number</div>
                                        )}
                                    </Form.Group>

                                    <Form.Group className="mb-4">
                                        <Form.Label className="profile-label">
                                            Languages spoken <span style={{color:'red'}}>*</span>
                                        </Form.Label>
                                        <CreatableSelect
                                            isMulti
                                            options={LANGUAGE_OPTIONS}
                                            value={languages}
                                            onChange={opts => setLanguages(opts || [])}
                                            placeholder="Select or type languages..."
                                            className="languages-select"
                                            menuPortalTarget={document.body}
                                            styles={{ menuPortal: base => ({ ...base, zIndex: 9999 }) }}
                                            onBlur={() => setLanguagesTouched(true)}
                                        />
                                        {languagesTouched && !languages.length && (
                                            <div style={{color:'red', fontSize:13, marginTop:4}}>Please select at least one language</div>
                                        )}
                                    </Form.Group>

                                    <Form.Group className="mb-4">
                                        <Form.Label className="profile-label">
                                            Tell us a little bit about yourself
                                        </Form.Label>
                                        <Form.Control
                                            as="textarea"
                                            className="profile-textarea"
                                            placeholder="e.g: Hey! I'm living in Prague for a student exchange. I'd love to connect with others and share a cozy Shabbat meal — always happy to eat some great Jewish food!"
                                            rows={3}
                                            value={about}
                                            onChange={handleTextareaChange}
                                            ref={textareaRef}
                                        />
                                    </Form.Group>

                                    <div className="text-center">
                                        <Button
                                            variant="warning"
                                            size="lg"
                                            className="profile-continue-btn"
                                            onClick={handleContinue}
                                        >
                                            Continue
                                        </Button>
                                    </div>
                                </Form>
                            </Card.Body>
                        </Card>
                    </Col>
                </Row>
            </Container>
        </div>
    );
}